import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, useReducedMotion } from 'framer-motion';
import { Button } from 'semantic-ui-react';
import ReCAPTCHA from 'react-google-recaptcha';
import { sendNotification } from '../utils/notify';
import { trackEvent } from '../utils/analytics';
import '../styles/ContactForm.scss';
import EmailSuggestion, { useEmailSuggestion } from './EmailSuggestion';
import { formatPhone } from '../utils/formatPhone';
import '../styles/EventRegistration.scss';

// Event details live here so they're trivial to edit. Time is a placeholder
// until confirmed.
const EVENT = {
  name: 'Buri Buri Fall Social',
  // Cloze tag on every RSVP, so Lily can pull this event's guest list.
  tag: 'buri-buri-social',
  date: 'Saturday, October 10, 2026',
  time: '1:30–3:30 PM',
  location: 'Buri Buri Park',
  city: 'South San Francisco',
  // Local Pacific start/end for the "Add to calendar" link; `calTz` lets Google
  // resolve the timezone/DST, so no manual UTC conversion is needed.
  calStart: '20261010T133000',
  calEnd: '20261010T153000',
  calTz: 'America/Los_Angeles',
};

// Google Calendar "add event" link, prefilled from EVENT. (Google's template links
// can't set reminders — it applies each user's default; the .ics carries alarms.)
const googleCalUrl = () => {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: `🍦 ${EVENT.name} 🍦`,
    dates: `${EVENT.calStart}/${EVENT.calEnd}`,
    ctz: EVENT.calTz,
    details: `You're all set for the ${EVENT.name}! 🎉\n\n🍦 Mister Softee ice cream truck\n🎈 Balloon animals\n🎨 Face painting\n🎟️ Community raffle\n\nFree & family-friendly — see you there!`,
    location: `${EVENT.location}, ${EVENT.city}, CA`,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

// Google Maps directions to the park.
const directionsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${EVENT.location}, ${EVENT.city}`)}`;

const calendarIcon = (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
    strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3.5" y="4.5" width="17" height="16" rx="2" />
    <path d="M3.5 9h17M8 3v3M16 3v3" />
  </svg>
);

// Line icons (stroke, currentColor) instead of emoji — consistent 24px grid,
// 1.6 stroke, so they read as one family and theme cleanly.
const svg = (children) => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

const ICONS = {
  iceCream: svg(<><path d="M7.5 10.5a4.5 4.5 0 0 1 9 0" /><path d="M6.8 10.5h10.4L12 21.3 6.8 10.5Z" /><path d="M9 14.2h6" /></>),
  balloon: svg(<><path d="M12 3.2c2.9 0 5 2.2 5 5 0 3.4-2.6 5.9-5 6.3-2.4-.4-5-2.9-5-6.3 0-2.8 2.1-5 5-5Z" /><path d="M12 14.5v1.6" /><path d="M11.2 16.1c1 .5 1.6 1.4 1.6 2.5S12 20.6 12 21.8" /></>),
  palette: svg(<><path d="M12 3.2a8.8 8.8 0 1 0 0 17.6c1.1 0 1.9-.9 1.9-1.9 0-.5-.2-.9-.5-1.2a1.7 1.7 0 0 1-.4-1.1c0-1 .8-1.7 1.8-1.7H16a4.9 4.9 0 0 0 4.8-4.9c0-3.8-3.9-6.8-8.8-6.8Z" /><circle cx="8" cy="10.5" r="1" /><circle cx="12" cy="7.8" r="1" /><circle cx="16" cy="10.5" r="1" /></>),
  ticket: svg(<><path d="M3.5 9.2V7.4a1 1 0 0 1 1-1h15a1 1 0 0 1 1 1v1.8a2 2 0 0 0 0 5.6v1.8a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1v-1.8a2 2 0 0 0 0-5.6Z" /><path d="M15 6.6v10.8" strokeDasharray="1.6 2" /></>),
  music: svg(<><path d="M9 17.5V5.2l10-2v12.3" /><circle cx="6.5" cy="17.5" r="2.5" /><circle cx="16.5" cy="15.5" r="2.5" /></>),
  neighbors: svg(<><circle cx="9" cy="8" r="3" /><path d="M3.5 19v-1.4A3.6 3.6 0 0 1 7.1 14h3.8a3.6 3.6 0 0 1 3.6 3.6V19" /><path d="M16 5.3a3 3 0 0 1 0 5.4" /><path d="M17.5 14.1a3.6 3.6 0 0 1 3 3.5V19" /></>),
};

// Each activity gets its own playful hue: a soft chip tint, a mid-tone badge,
// and a deeper icon color (glyph contrast on the badge stays >=3:1).
const ACTIVITIES = [
  { icon: ICONS.iceCream, label: 'Mister Softee ice cream truck', tint: '#FBEEF0', badge: '#F6D3D8', color: '#B34155' },
  { icon: ICONS.music, label: 'Live music', tint: '#EAF4EE', badge: '#CDE7D6', color: '#3E7A54' },
  { icon: ICONS.balloon, label: 'Balloon animals', tint: '#EDF3F9', badge: '#D5E3F2', color: '#2F6497' },
  { icon: ICONS.palette, label: 'Face painting', tint: '#F2ECF8', badge: '#E6DBF1', color: '#6E4E9E' },
  { icon: ICONS.neighbors, label: 'The good company of your neighbors', tint: '#E7F2F1', badge: '#CBE6E3', color: '#2E7A73' },
  { icon: ICONS.ticket, label: 'Community raffle', tint: '#FAF2E2', badge: '#F3E4C3', color: '#9C6B1E' },
];

const formContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07, delayChildren: 0.1 } },
};
const fieldItem = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
};

function EventRegistration() {
  const reduceMotion = useReducedMotion();
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [email, setEmail] = useState('');
  const emailHint = useEmailSuggestion(email);
  const [phone, setPhone] = useState('');
  const [sendError, setSendError] = useState('');
  const [captchaValue, setCaptchaValue] = useState(null);
  const form = useRef();
  const captchaRef = useRef();

  const handleCaptchaExpired = () => {
    setCaptchaValue(null);
    captchaRef.current?.reset();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!captchaValue) {
      alert('Please verify you are not a robot.');
      return;
    }
    setSending(true);
    setSendError('');

    const fd = new FormData(form.current);
    try {
      await sendNotification({
        type: 'event',
        event_name: EVENT.name,
        event_tag: EVENT.tag,
        event_when: `${EVENT.date} · ${EVENT.time}`,
        event_where: `${EVENT.location}, ${EVENT.city}`,
        event_gcal: googleCalUrl(),
        first_name: fd.get('first_name'),
        last_name: fd.get('last_name'),
        email: fd.get('email'),
        phone_number: fd.get('phone_number'),
        party_size: fd.get('party_size'),
        marketing_opt_in: fd.get('marketing_opt_in') ? 'Yes' : 'No',
      });
      setSending(false);
      setSent(true);
      trackEvent('event_rsvp_submit', { event: EVENT.name });
    } catch (err) {
      console.error('Event RSVP send failed:', err);
      setSending(false);
      setSendError('Something went wrong, please try again, or email lily@lilylaisf.com directly.');
      trackEvent('event_rsvp_error', { event: EVENT.name });
    }
  };

  return (
    <>
      <Helmet>
        <title>RSVP · {EVENT.name} | Lily Lai Real Estate</title>
        <meta name="description" content={`RSVP for ${EVENT.name} at ${EVENT.location} in ${EVENT.city} on ${EVENT.date}. A free, family-friendly community event with an ice cream truck, balloon animals, face painting, and a raffle.`} />
        <link rel="canonical" href="https://www.lilylaisf.com/events/fall-social" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.lilylaisf.com/events/fall-social" />
        <meta property="og:title" content={`RSVP · ${EVENT.name}`} />
        <meta property="og:description" content={`Join us at ${EVENT.location}, ${EVENT.city} on ${EVENT.date}. Free & family-friendly.`} />
        <meta property="og:image" content="https://www.lilylaisf.com/events/fall-social-og.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`RSVP · ${EVENT.name}`} />
        <meta name="twitter:description" content={`Join us at ${EVENT.location}, ${EVENT.city} on ${EVENT.date}. Free & family-friendly.`} />
        <meta name="twitter:image" content="https://www.lilylaisf.com/events/fall-social-og.png" />
      </Helmet>

      <div className="event-page" style={{ backgroundImage: "url('/events/fall-social-stripes.svg')" }}>
        <div className="event-card">
          <div className="event-details">
          <h1 className="event-title">{EVENT.name}</h1>
          <p className="event-tagline">Free · All ages welcome</p>

          <div className="event-meta">
            <div className="event-meta-row">
              <span className="event-meta-label">When</span>
              <span className="event-meta-value">{EVENT.date} · {EVENT.time}</span>
            </div>
            <div className="event-meta-row">
              <span className="event-meta-label">Where</span>
              <span className="event-meta-value">
                {EVENT.location}, {EVENT.city}
                <a
                  className="event-directions"
                  href={directionsUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackEvent('event_get_directions', { event: EVENT.name })}
                >Get directions →</a>
              </span>
            </div>
          </div>

          <p className="event-intro">
            Bring the whole family for a free afternoon in the park. Here's what to look
            forward to:
          </p>

          <ul className="event-activities">
            {ACTIVITIES.map(a => (
              <li key={a.label} className="event-activity" style={{ background: a.tint }}>
                <span className="event-activity-icon" style={{ background: a.badge, color: a.color }}>{a.icon}</span>
                <span className="event-activity-label">{a.label}</span>
              </li>
            ))}
          </ul>
          </div>

          <div className="event-rsvp">
          {sent ? (
            <div className="event-success" role="status">
              <h2 className="event-success-title">You're on the list!</h2>
              <p className="event-success-text">
                Thanks for registering. We'll see you at {EVENT.location} on {EVENT.date}.
                Save the date so it doesn't slip by:
              </p>
              <div className="event-cal-btns">
                <a
                  className="event-cal-btn"
                  href={googleCalUrl()}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackEvent('event_add_to_calendar', { event: EVENT.name, cal: 'google' })}
                >
                  {calendarIcon}
                  Google Calendar
                </a>
                <a
                  className="event-cal-btn event-cal-btn--secondary"
                  href="/events/buri-buri-fall-social.ics"
                  download
                  onClick={() => trackEvent('event_add_to_calendar', { event: EVENT.name, cal: 'ics' })}
                >
                  {calendarIcon}
                  Apple / Outlook
                </a>
              </div>
              <Link to="/" className="event-success-link">← Back to home</Link>
            </div>
          ) : (
            <>
              <p className="event-form-heading">Save me a spot!</p>
              <div className="contact-form event-form">
              <motion.form
                ref={form}
                onSubmit={submit}
                className="form-fields"
                variants={formContainer}
                initial={reduceMotion ? false : 'hidden'}
                animate="visible"
              >
                <motion.div className="form-field" variants={fieldItem}>
                  <label htmlFor="first_name">First Name</label>
                  <input id="first_name" type="text" name="first_name" required placeholder="Jane" autoComplete="given-name" />
                </motion.div>

                <motion.div className="form-field" variants={fieldItem}>
                  <label htmlFor="last_name">Last Name</label>
                  <input id="last_name" type="text" name="last_name" required placeholder="Doe" autoComplete="family-name" />
                </motion.div>

                <motion.div className="form-field" variants={fieldItem}>
                  <label htmlFor="email">Email Address</label>
                  <input id="email" type="email" name="email" required placeholder="jane@email.com" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} onBlur={emailHint.onBlur} />
                  <EmailSuggestion suggestion={emailHint.suggestion} onAccept={setEmail} />
                </motion.div>

                <motion.div className="form-field" variants={fieldItem}>
                  <label htmlFor="phone_number">Phone Number</label>
                  <input id="phone_number" type="tel" name="phone_number" required placeholder="(___) ___-____" autoComplete="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(formatPhone(e.target.value))} />
                </motion.div>

                <motion.div className="form-field" variants={fieldItem}>
                  <label htmlFor="party_size">How many are coming? (including you)</label>
                  <input id="party_size" type="number" name="party_size" required min="1" max="20" placeholder="4" inputMode="numeric" />
                </motion.div>

                <motion.div className="form-field" variants={fieldItem}>
                  <label className="event-optin">
                    <input type="checkbox" name="marketing_opt_in" value="yes" defaultChecked />
                    <span>Keep me in the loop with Lily's occasional Bay Area market updates.</span>
                  </label>
                </motion.div>

                <motion.div className="form-field" variants={fieldItem}>
                  <ReCAPTCHA
                    ref={captchaRef}
                    sitekey="6Ldx-mUsAAAAAF9gnrvdButSomCO_hFeC19NzFmw"
                    onChange={setCaptchaValue}
                    onExpired={handleCaptchaExpired}
                    onErrored={handleCaptchaExpired}
                    className="captcha"
                  />
                </motion.div>

                <motion.div className="form-field" variants={fieldItem}>
                  {!sending && <Button type="submit" basic disabled={!captchaValue}>Register</Button>}
                  {sending && <Button loading basic />}
                  {sendError && <p className="form-error" role="alert">{sendError}</p>}
                </motion.div>

                <motion.p className="event-disclaimer" variants={fieldItem}>
                  We'll use your info to share details about this event and can't wait to see you there. You can unsubscribe anytime. See our{' '}
                  <Link to="/privacypolicy" target="_blank" rel="noreferrer">Privacy Policy</Link>.
                </motion.p>
              </motion.form>
              </div>
            </>
          )}
          </div>
        </div>
      </div>
    </>
  );
}

export default EventRegistration;
